import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, Store as StoreIcon } from "lucide-react";
import type { Store } from "@nh/shared";
import { Button } from "@/shared/ui/core";
import { useAuth } from "@/features/auth/auth.context";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El cromo de una tienda PERSONALIZADA — deliberadamente distinto al de
 * It's The Best (notas de Anahí, 27-ago):
 *
 *  · La cabecera corona el NOMBRE de la tienda ("Annieshop") y a quien
 *    la atiende, no la marca de la casa.
 *  · El FOOTER es propio: dice de quién es la tienda y que corre con
 *    tecnología de It's The Best, con la puerta de vuelta a la tienda
 *    original.
 *  · CERRAR SESIÓN aquí te lleva a la tienda original (/shop) — nota 1
 *    de Anahí: saliste de la tienda de alguien, vuelves a la casa.
 * ═══════════════════════════════════════════════════════════════════
 */
export function StoreLayout({ store, children }: { store: Store; children: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-ds-cream">
      <header className="sticky top-0 z-50 w-full border-b border-ds-border bg-ds-cream/85 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between gap-3 px-4 py-3 sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            {/* El chip corona el COLOR del franquiciatario: su marca. */}
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-white"
              style={{ background: store.branding.brandColor }}
            >
              <StoreIcon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-[18px] font-semibold leading-tight text-ds-ink">
                {store.name}
              </p>
              <p className="text-[12px] text-ds-ink-3">
                {t("Tienda de {{name}} · It's The Best", { name: store.ownerName })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <span className="hidden text-[13px] font-medium text-ds-ink sm:inline">
                  {t("Hola, {{name}}", { name: user.firstName })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<LogOut />}
                  onClick={() => {
                    void logout();
                    navigate("/shop");
                  }}
                >
                  {t("Salir")}
                </Button>
              </>
            ) : (
              <Button as={Link} to="/login" variant="secondary" size="sm">
                {t("Iniciar sesión")}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-ds-border bg-white">
        <div className="mx-auto flex w-full max-w-[1200px] flex-wrap items-center justify-between gap-3 px-4 py-6 sm:px-8">
          <p className="text-[13px] text-ds-ink-3">
            © {store.name} · {t("Tienda personalizada de {{name}}", { name: store.ownerName })}
          </p>
          <p className="text-[13px] text-ds-ink-3">
            {t("Con tecnología de")}{" "}
            <Link
              to="/shop"
              className="font-medium text-ds-violet-pop underline-offset-2 hover:underline"
            >
              It's The Best
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
